import React from 'react';

import { CaraWorkspaceEffortLevel } from '@oysterjs/ais/api/graphql';
import { useOptionalCurrentAgencyUser } from '@oysterjs/ais/components/current-user';
import { FEATURE_FLAGS, useFlag } from '@oysterjs/ais/components/feature-flags';

export const EFFORT_LEVEL_LABELS: Record<CaraWorkspaceEffortLevel, string> = {
  [CaraWorkspaceEffortLevel.Auto]: 'Auto',
  [CaraWorkspaceEffortLevel.Low]: 'Quick',
  [CaraWorkspaceEffortLevel.LowMedium]: 'Quick',
  [CaraWorkspaceEffortLevel.Medium]: 'Standard',
  [CaraWorkspaceEffortLevel.High]: 'Extended'
};

/**
 * What the server runs instead of a level that cannot run: a Focused
 * workspace whose documents are too large, a preset default the user's plan
 * does not allow, or Auto while its gate is off.
 */
export const FALLBACK_EFFORT_LEVEL = CaraWorkspaceEffortLevel.Medium;

export const HIGH_EFFORT_UNAVAILABLE = 'Requires a usage-based plan.';

export const effortLevelLabel = (level: CaraWorkspaceEffortLevel): string =>
  EFFORT_LEVEL_LABELS[level];

/**
 * Whether Cara gave a Quick workspace more power partway through. It still
 * reads as Quick, because it keeps reading its documents the Quick way.
 */
export const effortRaisedWithinQuick = (workspace: {
  effortLevel?: CaraWorkspaceEffortLevel | null;
}): boolean => workspace.effortLevel === CaraWorkspaceEffortLevel.LowMedium;

export const effortLevelDescription = (level: CaraWorkspaceEffortLevel): string => {
  const fallback = effortLevelLabel(FALLBACK_EFFORT_LEVEL);
  switch (level) {
    case CaraWorkspaceEffortLevel.Auto:
      return "Automatically chooses the right analysis depth based on the task's complexity.";
    case CaraWorkspaceEffortLevel.Low:
    case CaraWorkspaceEffortLevel.LowMedium:
      return `Fastest results for simpler tasks, but may miss nuances in complex documents. Large documents switch back to ${fallback}.`;
    case CaraWorkspaceEffortLevel.Medium:
      return 'A careful review that catches details a quick read can miss. Recommended for most work.';
    case CaraWorkspaceEffortLevel.High:
      return "Cara's most thorough work, for complex, high-stakes tasks. Takes longer to complete.";
  }
};

export type EffortLevelChoice = {
  level: CaraWorkspaceEffortLevel;
  /** Why the level cannot run for the current selection; unset when it can. */
  unavailableReason?: string;
  /**
   * The level is unavailable only because of the agency's plan, so the selector
   * shows a link to upgrade in place of the reason.
   */
  requiresUpgrade?: boolean;
};

/**
 * What a new workspace asks for when the user leaves the choice alone: Auto
 * where it is offered, and Comprehensive where it is not.
 */
export const defaultEffortLevel = (
  choices: readonly EffortLevelChoice[]
): CaraWorkspaceEffortLevel =>
  choices.some((choice) => choice.level === CaraWorkspaceEffortLevel.Auto)
    ? CaraWorkspaceEffortLevel.Auto
    : FALLBACK_EFFORT_LEVEL;

/**
 * The level a submit sends for the one the user picked, or the default while
 * they have picked none. A pick that cannot run, such as Exhaustive off a
 * usage-based plan, sends the default instead, so it never reaches a server
 * that would refuse it.
 */
export const resolveEffortLevel = (
  selected: CaraWorkspaceEffortLevel | null | undefined,
  choices: readonly EffortLevelChoice[],
  fallback: CaraWorkspaceEffortLevel = defaultEffortLevel(choices)
): CaraWorkspaceEffortLevel => {
  const choice = choices.find((c) => c.level === selected);
  return choice && !choice.unavailableReason ? choice.level : fallback;
};

/**
 * The level a custom preset's run starts at: its saved default, or the default
 * when it has none. A saved default this user cannot run, Auto included while
 * its gate is off, starts at Comprehensive, as the server would run it.
 */
export const initialEffortLevel = (
  preferred: CaraWorkspaceEffortLevel | null | undefined,
  choices: readonly EffortLevelChoice[]
): CaraWorkspaceEffortLevel =>
  preferred
    ? resolveEffortLevel(preferred, choices, FALLBACK_EFFORT_LEVEL)
    : defaultEffortLevel(choices);

/**
 * Whether the effort selector is shown, and the levels it offers.
 *
 * Auto comes first while its own gate is on as well as the selector's, and is
 * then always available: the server only lets it choose a level the request
 * could have named. Focused is offered wherever the selector is, because the
 * selector's gate is what opens LOW on the server. Exhaustive is always listed,
 * so a user on another plan learns it exists, but is unavailable unless
 * AgencyUser.highEffortEnabled.
 */
export const useEffortLevelChoices = (): {
  enabled: boolean;
  choices: EffortLevelChoice[];
  highEnabled: boolean;
} => {
  const enabled = useFlag(FEATURE_FLAGS.EFFORT_LEVEL_SELECTOR);
  const autoFlag = useFlag(FEATURE_FLAGS.EFFORT_LEVEL_AUTO);
  const highEnabled = useOptionalCurrentAgencyUser()?.highEffortEnabled ?? false;

  const choices: EffortLevelChoice[] = [];
  if (enabled && autoFlag) {
    choices.push({ level: CaraWorkspaceEffortLevel.Auto });
  }
  if (enabled) {
    choices.push({ level: CaraWorkspaceEffortLevel.Low });
  }
  choices.push({ level: CaraWorkspaceEffortLevel.Medium });
  choices.push({
    level: CaraWorkspaceEffortLevel.High,
    unavailableReason: highEnabled ? undefined : HIGH_EFFORT_UNAVAILABLE,
    requiresUpgrade: !highEnabled
  });

  return { enabled, choices, highEnabled };
};

export type EffortPicker = {
  value: CaraWorkspaceEffortLevel;
  onChange: (next: CaraWorkspaceEffortLevel) => void;
  choices: readonly EffortLevelChoice[];
};

/**
 * Effort state for the Ask Cara box, which starts a workspace with no preset.
 * `picker` is unset while the selector is off, so the box shows no control, and
 * `level` is what the create sends: unset while off, so the server applies its
 * default.
 */
export const useComposeEffortLevel = (): {
  picker?: EffortPicker;
  level?: CaraWorkspaceEffortLevel;
} => {
  const { enabled, choices } = useEffortLevelChoices();
  const [selected, setSelected] = React.useState<CaraWorkspaceEffortLevel>();
  if (!enabled) {
    return {};
  }
  const level = resolveEffortLevel(selected, choices);
  return { picker: { value: level, onChange: setSelected, choices }, level };
};
